import { BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline';

import { type EvidenceData } from '@/components/citations';
import {
  AddressDisplay,
  buildFullName,
  ContactHandleList,
  EntityCard,
  EntityCardHeader,
  EntityCardSection,
  EntityDisclosure,
  formatEnum,
} from '@/components/shared';
import { FieldWithApproval } from '@/components/suggestions';
import type {
  ReadOrganization,
  ReadOrganizationWithEmployees,
} from '@/services/api/partiesService';

interface OrganizationCardProps {
  organization: ReadOrganization | ReadOrganizationWithEmployees;
  showEmployees?: boolean;
  onEdit?: () => void;
  onApproveField?: (fieldKey: string) => void;
  showApproval?: boolean;
  evidence?: EvidenceData[];
  evidenceTitle?: string;
  matterId?: string;
}

export const OrganizationCard = ({
  organization,
  showEmployees = false,
  onEdit,
  onApproveField,
  showApproval = false,
  evidence,
  evidenceTitle,
  matterId,
}: OrganizationCardProps) => {
  const displayName =
    organization.legal_name ||
    organization.dba_name ||
    organization.short_description ||
    'Unnamed Organization';

  const hasEmployees =
    showEmployees &&
    'employees' in organization &&
    organization.employees !== undefined &&
    organization.employees.length > 0;

  const hasContactInfo = (organization.contact_handles?.length ?? 0) > 0;
  const hasAddress = organization.address_line1 || organization.city;
  const hasAdditionalDetails =
    hasContactInfo || hasAddress || organization.tax_id;

  const badges = [
    organization.identity_status &&
      organization.identity_status !== 'named' && (
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 capitalize">
          {organization.identity_status}
        </span>
      ),
  ].filter(Boolean);

  return (
    <EntityCard
      icon={<BuildingOfficeIcon />}
      isClaimant={organization.is_claimant}
      approvalMetadata={organization.approval_metadata}
      showApproval={showApproval}
      onEdit={onEdit}
      evidence={evidence}
      evidenceTitle={evidenceTitle}
      matterId={matterId}
    >
      <EntityCardHeader
        title={displayName}
        isClaimant={organization.is_claimant}
        badges={badges}
      />

      {organization.dba_name && organization.legal_name && (
        <p className="mt-0.5 text-sm text-gray-600">
          dba {organization.dba_name}
        </p>
      )}

      {organization.involvement_description && (
        <p className="mt-1 text-sm text-gray-600">
          {organization.involvement_description}
        </p>
      )}

      <div className="mt-1 text-sm text-gray-500 capitalize">
        {formatEnum(organization.organization_type)}
      </div>

      {/* Compact contact info */}
      {!showApproval && organization.contact_handles && (
        <EntityCardSection className="mt-2">
          <ContactHandleList handles={organization.contact_handles} compact />
        </EntityCardSection>
      )}

      {/* Employees */}
      {hasEmployees && (
        <EntityCardSection className="mt-2 space-y-1.5">
          {(organization as ReadOrganizationWithEmployees).employees?.map(
            (employee) => {
              const empName = buildFullName(
                employee.first_name,
                undefined,
                employee.last_name,
              );
              return (
                <div
                  key={employee.id}
                  className="flex items-center gap-1.5 text-sm"
                >
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <span className="text-xs text-gray-500">Employee · </span>
                    <span className="text-gray-900 font-medium">
                      {empName || employee.short_description || 'Unnamed'}
                    </span>
                  </div>
                </div>
              );
            },
          )}
        </EntityCardSection>
      )}

      {/* Details disclosure */}
      {hasAdditionalDetails && (
        <EntityDisclosure>
          {showApproval && onApproveField ? (
            <dl className="space-y-2">
              <FieldWithApproval
                label="Organization Type"
                value={formatEnum(organization.organization_type)}
                fieldKey="organization_type"
                approvalMetadata={organization.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Tax ID"
                value={organization.tax_id}
                fieldKey="tax_id"
                approvalMetadata={organization.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Legal Name"
                value={organization.legal_name}
                fieldKey="legal_name"
                approvalMetadata={organization.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="DBA Name"
                value={organization.dba_name}
                fieldKey="dba_name"
                approvalMetadata={organization.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
            </dl>
          ) : (
            organization.tax_id && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Tax ID
                </div>
                <div className="text-sm text-gray-900">
                  {organization.tax_id}
                </div>
              </div>
            )
          )}

          {hasContactInfo && organization.contact_handles && (
            <ContactHandleList handles={organization.contact_handles} />
          )}

          {hasAddress && (
            <AddressDisplay
              line1={organization.address_line1}
              line2={organization.address_line2}
              city={organization.city}
              state={organization.state}
              zip={organization.zip}
            />
          )}
        </EntityDisclosure>
      )}
    </EntityCard>
  );
};
